import styles from "./styles/SchoolsDropdown.module.scss";
import FontAwesome from "../utilities/FontAwesome";
import { useRef, useState } from "react";
import ClickableLink from "../utilities/ClickableLink";
import classNames from "classnames";
import { Box, DropdownMenu, Flex, Heading, Link } from "@radix-ui/themes";
import { Collapsible } from "radix-ui";
import { useStores } from "../../stores/useStores";
import { TranslatedText } from "../utilities/TranslatedText";

type SchoolLinkGroup = [category: string, links: SchoolLink[]];

function useSchoolLinkGroups(
  heroBlock: IHeroTerraceBlock,
): SchoolLinkGroup[] | null {
  const { organizationStore } = useStores();

  const isSubPage =
    organizationStore.currentOrganization.id !==
    organizationStore.organization.id;
  if (!heroBlock.school_links?.length || isSubPage) {
    return null;
  }

  const grouped = heroBlock.school_links.reduce<Record<string, SchoolLink[]>>(
    (acc, link) => {
      (acc[link.category] = acc[link.category] || []).push(link);
      return acc;
    },
    {},
  );
  return Object.entries(grouped).sort(
    ([a], [b]) => Number(b === "District") - Number(a === "District"),
  );
}

export function SchoolsDropdown(props: { heroBlock: IHeroTerraceBlock }) {
  const linkGroups = useSchoolLinkGroups(props.heroBlock);

  if (!linkGroups) {
    return null;
  }

  return (
    <div className={styles.container}>
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger>
          <button className={styles.trigger}>
            <TranslatedText>Our Schools</TranslatedText>&nbsp;
            <FontAwesome prefix={"fas"} name={"fa-arrow-up"} />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          side={"top"}
          align={"start"}
          sideOffset={10}
          avoidCollisions={false}
          style={{
            maxHeight:
              "min(60vh, var(--radix-dropdown-menu-content-available-height))",
          }}
        >
          {linkGroups.map(([category, links]) => (
            <DropdownMenu.Group key={category}>
              <DropdownMenu.Label
                style={{ fontWeight: "bold", color: "black" }}
              >
                {category}
              </DropdownMenu.Label>
              {links.map((link) => (
                <DropdownMenu.Item
                  key={link.title}
                  className={styles.menuItem}
                  asChild
                >
                  <ClickableLink href={link.url}>{link.title}</ClickableLink>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Group>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </div>
  );
}

export function MobileSchoolsDropdown(props: { heroBlock: IHeroTerraceBlock }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const linkGroups = useSchoolLinkGroups(props.heroBlock);

  if (!linkGroups) {
    return null;
  }

  return (
    <Collapsible.Root
      ref={ref}
      className={styles.mobileContainer}
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
    >
      <Collapsible.Trigger
        className={classNames(styles.trigger, {
          [styles.triggerSticky]: open,
        })}
      >
        <span className={styles.buttonTextContainer}>
          <TranslatedText>Our Schools</TranslatedText>&nbsp;
          <FontAwesome
            className={styles.faIcon}
            prefix={"fas"}
            name={open ? "fa-angle-down" : "fa-angle-right"}
          />
        </span>
      </Collapsible.Trigger>
      <Collapsible.Content className={styles.mobileContent}>
        <Flex direction={"column"} gap={"4"} p={"4"}>
          {linkGroups.map(([category, links]) => (
            <Box key={category}>
              <Heading as={"h2"} size={"4"} mb={"2"}>
                {category}
              </Heading>
              <Flex asChild direction={"column"} gap={"2"} m={"0"} p={"0"}>
                <ul>
                  {links.map((link) => (
                    <li key={link.title}>
                      <Link asChild size={"4"}>
                        <ClickableLink href={link.url}>
                          {link.title}
                        </ClickableLink>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Flex>
            </Box>
          ))}
        </Flex>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
